/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useState, useEffect } from "react";
import { DATA_TABLE_GUTTER_CLASS } from "./gutter";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import {
  FaAngleLeft,
  FaAngleRight,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { AiOutlineExport } from "react-icons/ai";
// import { MdOutlineAddCircleOutline } from "react-icons/md";
import "./DataTable.css";
import { dateFormate } from "util/Util";
import { truncateChars, shouldTruncate, DEFAULT_MAX_CELL_CHARS } from "util/truncate";
import Tooltip from "components/Tooltip";
import { useTranslation } from "react-i18next";
import FilterButton from "components/FilterButton/FilterButton";
import { colors } from "themes";

export interface Column {
  bold?: boolean;
  key: string;
  label: string;
  actions?: (row: Row) => React.ReactNode;
  excludeExport?: boolean;
  type?: string;
  render?: (value: ReactNode, row: Row) => React.ReactNode;
  className?: React.CSSProperties | string;
  noTruncate?: boolean;
  // Max characters before plain text/number cell content is truncated (with an
  // ellipsis + hover tooltip). Defaults to DEFAULT_MAX_CELL_CHARS. Ignored when
  // `noTruncate` is set or when the cell renders JSX (inputs, badges, etc.).
  maxChars?: number;
}

export interface Row {
  id: string | number;
  [key: string]: ReactNode;
}

export interface DataTableProps {
  data: Row[];
  columns: Column[];
  title?: string;
  onAddBtnClick?: () => void;
  loading?: boolean;
  itemsPerPage?: number;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  isShowFilter?: boolean;
  isExportable?: boolean;
  onApply?: (filters: any) => void;
  statusList?: { value: string; label: string }[];
  userList?: { value: string; label: string }[];
  customButton?: ReactNode;
  preAddButtons?: ReactNode;
  buttonContent?: string;
  hidePagination?: boolean;
  leftSideButtons?: ReactNode;
  nextSearchBarButton?: ReactNode;
  actionsContainerClassName?: string;
  serverSidePagination?: boolean;
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onSearchTermChange?: (searchTerm: string) => void;
  searchDebounceMs?: number;
  searchWidth?: string;
  searchAutoComplete?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
  hideSearchBar?: boolean;
  headerLabelClassName?: string;
  heightClass?: string; // Custom Height Prop
  // Optional padding overrides for denser rows. Default to the standard
  // spacing so existing tables are unaffected; pass tighter values (e.g.
  // "px-3 py-2") where a compact layout is needed.
  cellClassName?: string;
  headerCellClassName?: string;
  paginationContainerClassName?: string;
  hasActiveFilters?: boolean;
  centerToolbarElement?: React.ReactNode;
}

const filterRowsBySearch = (rows: Row[], searchTerm: string) => {
  if (!searchTerm) return rows;
  const normalizedTerm = searchTerm.toLowerCase();

  const getSearchableText = (value: ReactNode) => {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      typeof value === "bigint"
    ) {
      return String(value);
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    return "";
  };

  return rows.filter((row) =>
    Object.values(row).some((value) =>
      getSearchableText(value).toLowerCase().includes(normalizedTerm)
    )
  );
};

const getShowingRangeText = (
  totalPages: number,
  activeCurrentPage: number,
  itemsPerPage: number,
  totalRecords: number
) => {
  if (totalPages <= 0) {
    return "0";
  }

  const start = activeCurrentPage * itemsPerPage - itemsPerPage + 1;
  const end =
    activeCurrentPage === totalPages
      ? totalRecords
      : activeCurrentPage * itemsPerPage;

  return `${start}-${end}`;
};

const sortRows = (
  rows: Row[],
  sortColumn: string | null,
  sortDirection: "asc" | "desc"
) => {
  if (!sortColumn) return rows;

  return [...rows].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
};

const getRowSerialNumber = (
  activeCurrentPage: number,
  itemsPerPage: number,
  index: number
) => {
  if (activeCurrentPage === 1) {
    return activeCurrentPage + index;
  }
  return (activeCurrentPage - 1) * itemsPerPage + index + 1;
};

// Globally cap plain text/number cell content by character length so one long
// value can't blow out a column. JSX content (inputs, badges, dropdowns) and
// `noTruncate` columns pass through untouched; the full value stays available in
// a hover tooltip (title).
const capContent = (column: Column, content: ReactNode): ReactNode => {
  if (column.noTruncate) return content;
  if (typeof content !== "string" && typeof content !== "number") return content;
  const maxChars = column.maxChars ?? DEFAULT_MAX_CELL_CHARS;
  if (!shouldTruncate(content, maxChars)) return content;
  const full = String(content);
  return (
    <span title={full} className="whitespace-nowrap">
      {truncateChars(content, maxChars)}
    </span>
  );
};

const renderCellContent = (
  column: Column,
  row: Row,
  index: number,
  activeCurrentPage: number,
  itemsPerPage: number
) => {
  const columnClassName =
    typeof column.className === "string" ? column.className : "";
  const truncationClass = column.noTruncate
    ? "max-w-none overflow-visible whitespace-nowrap"
    : "max-w-sm overflow-hidden truncate xl:max-w-xs";

  if (column.key === "actions") {
    return column.actions?.(row);
  }

  if (column.type === "Date") {
    return (
      <span
        className={` ${column.bold ? "font-semibold text-gray-900 dark:text-white" : "font-normal text-gray-500"} ${columnClassName} ${truncationClass} text-base dark:text-[#E3E3E3]`}
      >
        {capContent(column, dateFormate(row[column.key] + ""))}
      </span>
    );
  }

  if (column.render) {
    return capContent(column, column.render(row[column.key], row));
  }

  if (column.key === "id") {
    return <strong>{getRowSerialNumber(activeCurrentPage, itemsPerPage, index)}</strong>;
  }

  return capContent(column, row[column.key]);
};

const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  title = "",
  onAddBtnClick,
  loading = false,
  itemsPerPage: propItemsPerPage,
  onItemsPerPageChange,
  isShowFilter = false,
  isExportable = false,
  onApply,
  statusList = [],
  userList = [],
  customButton,
  preAddButtons,
  buttonContent,
  hidePagination = false,
  leftSideButtons,
  nextSearchBarButton,
  searchWidth = "w-48 sm:w-56 lg:w-72 xl:w-96 2xl:w-[28rem]",
  actionsContainerClassName = "flex items-center ml-auto space-x-2 sm:space-x-3",
  serverSidePagination = false,
  currentPage: controlledCurrentPage,
  totalItems,
  onPageChange,
  onSearchTermChange,
  searchDebounceMs = 500,
  searchAutoComplete = "on",
  searchPlaceholder,
  showSearch = true,
  hideSearchBar = false,
  headerLabelClassName = "font-medium",
  heightClass = "h-[calc(100vh-65px)] min-h-[300px]", // Retains the exact default for all other components
  cellClassName = "p-3", // Default body-cell padding (unchanged for existing tables)
  headerCellClassName = "p-4", // Default header-cell padding (unchanged for existing tables)
  paginationContainerClassName = "",
  hasActiveFilters = false,
  centerToolbarElement,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [paginatedData, setPaginatedData] = useState<Row[]>([]);
  const [filteredData, setFilterData] = useState<Row[]>([]);
  const [localItemsPerPage, setLocalItemsPerPage] = useState<number>(10);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [hasFetched, setHasFetched] = useState(false);
  const { t } = useTranslation();

  const activeCurrentPage = serverSidePagination
    ? controlledCurrentPage || 1
    : currentPage;

  const totalRecords = serverSidePagination
    ? totalItems ?? filteredData.length
    : filteredData.length;

  const itemsPerPage = propItemsPerPage || localItemsPerPage;
  const isSearchVisible = showSearch && !hideSearchBar;

  useEffect(() => {
    if (!isSearchVisible || !onSearchTermChange) {
      setSearchTerm("");
    }
  }, [data, onSearchTermChange, isSearchVisible]);

  useEffect(() => {
    if (!serverSidePagination) {
      setCurrentPage(1);
    }
  }, [searchTerm, serverSidePagination]);

  useEffect(() => {
    if (!isSearchVisible) return;
    if (!onSearchTermChange) return;
    const timer = window.setTimeout(() => {
      onSearchTermChange(searchTerm);
    }, searchDebounceMs);

    return () => window.clearTimeout(timer);
  }, [searchTerm, onSearchTermChange, searchDebounceMs, isSearchVisible]);

  useEffect(() => {
    const activeSearchTerm = isSearchVisible ? searchTerm : "";
    const searchedRows = serverSidePagination
      ? [...data]
      : filterRowsBySearch([...data], activeSearchTerm);
    const sortedRows = sortRows(searchedRows, sortColumn, sortDirection);
    setFilterData(sortedRows);
  }, [searchTerm, sortColumn, sortDirection, data, serverSidePagination, isSearchVisible]);

  useEffect(() => {
    if (serverSidePagination) {
      setPaginatedData(filteredData);
      const calculatedPages = Math.ceil((totalItems ?? filteredData.length) / itemsPerPage);
      setTotalPages(calculatedPages || 0);
      return;
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
    const calculatedPages = Math.ceil(filteredData.length / itemsPerPage);
    setTotalPages(calculatedPages);
  }, [currentPage, itemsPerPage, filteredData, serverSidePagination, totalItems]);

  useEffect(() => {
    if (!loading) {
      setHasFetched(true);
    }
  }, [loading, data]);

  const handlePrevPage = () => {
    if (serverSidePagination && onPageChange) {
      onPageChange(Math.max(1, activeCurrentPage - 1));
      return;
    }
    setCurrentPage((prevPage) => Math.max(1, prevPage - 1));
  };

  const handleNextPage = () => {
    if (serverSidePagination && onPageChange) {
      onPageChange(Math.min(totalPages, activeCurrentPage + 1));
      return;
    }
    setCurrentPage((prevPage) => Math.min(totalPages, prevPage + 1));
  };

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");
    const exportColumns = columns.filter((column) => !column.excludeExport);
    const columnsKeys = exportColumns.map((column) => column.key);
    const rows = data.map((data1) => columnsKeys.map((key) => data1[key]));
    worksheet.addRow(columnsKeys);
    rows.forEach((row) => worksheet.addRow(row));
    const blob = await workbook.xlsx.writeBuffer();
    const fileName = title ? `${title}.xlsx` : `exported_data.xlsx`;
    saveAs(
      new Blob([blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName,
    );
  };

  const handleAddClickEvent = () => {
    if (onAddBtnClick) onAddBtnClick();
  };

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newValue = parseInt(e.target.value);
    if (onItemsPerPageChange) {
      onItemsPerPageChange(newValue);
    } else {
      setLocalItemsPerPage(newValue);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(key);
      setSortDirection("asc");
    }
  };

  const renderSortIndicator = (columnKey: string) => {
    if (sortColumn === columnKey) {
      return sortDirection === "asc" ? (
        <div className="center" style={{ marginBottom: -5, marginLeft: 5 }}>
          <FaSortUp />
        </div>
      ) : (
        <div className="center" style={{ marginTop: -5, marginLeft: 5 }}>
          <FaSortDown />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className={`${DATA_TABLE_GUTTER_CLASS} pt-1 bg-white block sm:flex items-center justify-between dark:bg-gray-800`}>
        <div className="w-full pb-1 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 sm:text-xl dark:text-white">
              {t("table.heading", { title: title })}
            </h1>
          </div>
          <div className="flex flex-wrap items-start sm:items-center gap-2 p-0 ml-0.5 mt-1 sm:mt-0 min-w-0">
            {/* Search Bar */}
            {isSearchVisible && (
              <div className="flex items-center mb-2 sm:mb-0 gap-2">
                <form action="#" method="GET">
                  <label htmlFor="users-search" className="sr-only">
                    {t("search")}
                  </label>
                  <div className={`relative mt-1 sm:mt-0 ${searchWidth}`}>
                    <input
                      type="text"
                      name="email"
                      id="users-search"
                      autoComplete={searchAutoComplete}
                      className="block w-full p-2.5 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:border-primary"
                      placeholder={searchPlaceholder || t("Search...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </form>
                {nextSearchBarButton && (
                  <div className="flex items-center gap-2">
                    {nextSearchBarButton}
                  </div>
                )}
              </div>
            )}
            {centerToolbarElement && (
              <div className="flex-1 flex justify-center px-4 overflow-hidden min-w-0">
                {centerToolbarElement}
              </div>
            )}
            {!isSearchVisible && nextSearchBarButton && (
              <div className="flex items-center gap-2 mb-2 sm:mb-0">
                {nextSearchBarButton}
              </div>
            )}

            {leftSideButtons && (
              <div className="flex items-center gap-2 mb-2 sm:mb-0">
                {leftSideButtons}
              </div>
            )}

            {isShowFilter && (
              <div className="mt-1">
                <FilterButton
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onApply={onApply ?? (() => { })}
                  statusList={statusList}
                  userList={userList}
                />
              </div>
            )}

            <div className={actionsContainerClassName}>
              {preAddButtons}

              {customButton && (
                <Tooltip content={buttonContent}>{customButton}</Tooltip>
              )}

              {onAddBtnClick && (
                <Tooltip content="Click to add">
                  <button
                    onClick={handleAddClickEvent}
                    style={{ backgroundColor: colors.primary }}
                    className="text-white hover:opacity-90 shadow-lg active:scale-95 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 transition-all"
                  >
                    {/* <MdOutlineAddCircleOutline
                      size={18}
                      style={{ marginRight: 5 }}
                    /> */}
                    {t("table.heading", { title: `${t("Add")}` })}
                  </button>
                </Tooltip>
              )}
              {isExportable && (filteredData.length || data.length) ? (
                <Tooltip content="Click to export">
                  <button
                    onClick={handleExportToExcel}
                    className="inline-flex items-center py-2.5 px-5 me-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                  >
                    <AiOutlineExport size={18} style={{ marginRight: 5 }} />
                    {t("Export")}
                  </button>
                </Tooltip>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`${DATA_TABLE_GUTTER_CLASS} flex flex-col ${heightClass ?? ""}`}>
        <div className={heightClass ? 'flex-1 overflow-auto min-h-0' : 'overflow-auto'}>
          <div className="inline-block w-full align-middle h-full">
            <table className="min-w-full table-auto table-fixed border-separate border-spacing-0">
              <thead className="sticky top-0 z-20 bg-white dark:bg-gray-800">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={`${headerCellClassName} text-md ${headerLabelClassName} text-left text-gray-500 capitalize cursor-pointer dark:text-gray-400 shadow-[inset_0_-1px_0_0_#e5e7eb] dark:shadow-[inset_0_-1px_0_0_#4b5563]`}
                      onClick={() => handleSort(column.key)}
                    >
                      <div className="flex items-center">
                        {t("table.columnlabel", { columnlabel: column.label })}
                        {renderSortIndicator(column.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800">
                {loading || !hasFetched ? (
                  Array.from({ length: Math.min(itemsPerPage, 10) }).map((_, i) => (
                    <tr key={`skeleton-${i}`}>
                      {columns.map((_, j) => (
                        <td
                          key={j}
                          className="p-3 border-b border-gray-200 dark:border-gray-700"
                        >
                          <div
                            className={`animate-pulse h-4 rounded bg-gray-200 dark:bg-gray-600 ${j % 3 === 0 ? "w-1/2" : j % 3 === 1 ? "w-3/4" : "w-2/3"
                              }`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {columns.map((column, index1) => {
                        const truncationClass = column.noTruncate
                          ? "max-w-none overflow-visible whitespace-nowrap"
                          : "max-w-sm overflow-hidden truncate xl:max-w-xs";
                        const customClass = typeof column.className === "string" ? column.className : "";

                        return (
                          <td
                            scope="row"
                            className={` ${column.bold ? "font-semibold text-gray-900" : "text-gray-500"} ${customClass} ${cellClassName} text-base font-normal text-gray-500 dark:text-[#E3E3E3] border-b border-gray-200 dark:border-gray-700 ${truncationClass}`}
                            key={index1}
                          >
                            {renderCellContent(column, row, index, activeCurrentPage, itemsPerPage)}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length}>
                      {searchTerm ? (
                        <div className="flex flex-col items-center justify-center min-h-[300px] py-12 px-4 text-center">
                          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                            <svg className="w-7 h-7 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l2 2" />
                            </svg>
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No results found</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-xs">
                            No matches for{" "}
                            <span className="font-medium text-gray-700 dark:text-gray-300">"{searchTerm}"</span>.
                            Try a different search term.
                          </p>
                          <button
                            onClick={() => setSearchTerm("")}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Clear search
                          </button>
                        </div>
                      ) : hasActiveFilters ? (
                        <div className="flex flex-col items-center justify-center min-h-[300px] py-12 px-4 text-center">
                          <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-4">
                            <svg className="w-7 h-7 text-amber-400 dark:text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 .78 1.625L14 12.382V19a1 1 0 0 1-1.447.894l-4-2A1 1 0 0 1 8 17v-4.618L3.22 5.625A1 1 0 0 1 3 5V4z" />
                            </svg>
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No matching records</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                            No data matches the active filters. Try adjusting or clearing your filters.
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center min-h-[300px] py-12 px-4 text-center">
                          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                            <svg className="w-7 h-7 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7m16 0v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5m16 0H4m8-4v4m0 0-2-2m2 2 2-2" />
                            </svg>
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No data yet</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-xs">
                            {onAddBtnClick ? "Get started by adding your first record." : "There's nothing here yet."}
                          </p>
                          {onAddBtnClick && (
                            <button
                              onClick={onAddBtnClick}
                              style={{ backgroundColor: colors.primary }}
                              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white hover:opacity-90 transition-opacity"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Add new
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {!hidePagination && (loading || !hasFetched || paginatedData.length > 0) && (
          <div
            className={`flex-shrink-0 items-center w-full pt-4 pb-0 py-2 pl-4 pr-6 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 sm:flex sm:justify-between ${paginationContainerClassName}`}
          >
            <div className="flex items-center mb-0 sm:mb-0">
              <select
                id="small"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                onChange={handleItemsPerPageChange}
                value={itemsPerPage.toString()}
                disabled={loading || !hasFetched}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              {loading || !hasFetched ? (
                <div className="animate-pulse ml-2 h-4 w-40 bg-gray-200 dark:bg-gray-600 rounded" />
              ) : (
                <span className="counting text-base font-normal text-gray-500 dark:text-[#E3E3E3] ml-2">
                  Showing{" "}
                  <span className="counting dark:text-[#E3E3E3]">
                    {getShowingRangeText(
                      totalPages,
                      activeCurrentPage,
                      itemsPerPage,
                      totalRecords
                    )}
                  </span>{" "}
                  out of{" "}
                  <span className="counting dark:text-[#E3E3E3]">
                    {totalRecords}
                  </span>
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {loading || !hasFetched ? (
                <div className="animate-pulse flex gap-2">
                  <div className="h-9 w-24 bg-gray-200 dark:bg-gray-600 rounded-lg" />
                  <div className="h-9 w-20 bg-gray-200 dark:bg-gray-600 rounded-lg" />
                </div>
              ) : (
                <>
                  {activeCurrentPage !== 1 && (
                    <Tooltip content="Click to go Previous">
                      <button
                        onClick={handlePrevPage}
                        className="action-btn btn-primary px-3 py-2 mr-1 rounded-lg"
                      >
                        <FaAngleLeft />
                        {t("Previous")}
                      </button>
                    </Tooltip>
                  )}
                  {activeCurrentPage * itemsPerPage < totalRecords && (
                    <Tooltip content="Click to go Next">
                      <button
                        onClick={handleNextPage}
                        className="action-btn btn-primary px-3 py-2 rounded-lg"
                      >
                        {t("Next")}
                        <FaAngleRight className="ml-1" />
                      </button>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DataTable;